import type { TaskListListOutput, TaskListType } from "@orsane/schema/task-lists";
import { BuiltInAttribute, type TaskOutputWithNewlyCreated, type TaskStatusOutput } from "@orsane/schema/tasks";
import type { UserOutput } from "@orsane/schema/users";
import { addDays, addMonths, addWeeks, type Day, endOfMonth, endOfWeek, format, subDays } from "date-fns";

export type DraftTaskQuery = {
    type?: TaskListType;
    parentTask?: {
        id: number;
        key: string;
        number: number;
        title: string;
        taskLists: TaskListListOutput[];
    } | null;
};

/**
 * Builds the unsaved row inserted when the user clicks "Add task" (or pastes).
 * `currentUser` comes from the auth session. A missing user means the session
 * is still loading or has expired while the list route stayed mounted.
 */
export function buildDraftTask(input: {
    title: string;
    groupColumnId: string | null;
    groupValue: any;
    currentUser: UserOutput | null;
    taskStatuses: TaskStatusOutput[] | undefined;
    queryParams: DraftTaskQuery;
    currentTaskList: TaskListListOutput | null | undefined;
    users: UserOutput[] | undefined;
    taskLists: TaskListListOutput[] | undefined;
    weekStartsOn: Day;
}): TaskOutputWithNewlyCreated | null {
    const {
        title,
        groupColumnId,
        groupValue,
        currentUser,
        taskStatuses,
        queryParams,
        currentTaskList,
        users,
        taskLists,
        weekStartsOn,
    } = input;

    if (!taskStatuses) {
        throw new Error("No task statuses found. Should not happen.");
    }
    // The list route only checks the session in beforeLoad, so an expired session
    // leaves this page mounted. Throwing here became an unhandled click error
    // (ORSANE-3X). Callers send the user to login instead.
    if (!currentUser) {
        return null;
    }

    const defaultStatus = taskStatuses.find((status) => status.isDefault);
    if (defaultStatus == null || defaultStatus === undefined) {
        throw new Error("No default task status found");
    }

    const tempTask: TaskOutputWithNewlyCreated = {
        id: -Date.now(),
        key: crypto.randomUUID(),
        number: 0,
        hasDescription: false,
        hasActivity: false,
        title: title,
        taskStatusId: defaultStatus.id,
        taskStatus: defaultStatus,
        dueDate: null,
        createdAt: new Date(),
        updatedAt: new Date(),
        priority: null,
        justCreated: false,
        forcedSortOrder: Number.MAX_SAFE_INTEGER,
        taskLists: currentTaskList ? [currentTaskList] : [],
        assignees: [],
        isNewlyCreated: true,
        customAttributeValues: [],
        createdBy: currentUser,
        parentTask: null,
        // Omitted previously; the row model treats a missing flag as a match.
        isFilterMatch: true,
    };

    if (queryParams.parentTask) {
        tempTask.parentTask = {
            id: queryParams.parentTask.id,
            key: queryParams.parentTask.key,
            number: queryParams.parentTask.number,
            title: queryParams.parentTask.title,
        };

        tempTask.taskLists = [...queryParams.parentTask.taskLists];
    }

    if (!!groupColumnId && !!groupValue) {
        const groupingProperty = groupColumnId;

        if (groupingProperty === BuiltInAttribute.taskStatus) {
            const status = taskStatuses?.find((status) => status.order === groupValue);
            if (!status) {
                throw new Error(`No status found for order ${groupValue}`);
            }
            tempTask.taskStatusId = status.id;
            tempTask[groupingProperty] = status;
        } else if (groupingProperty === BuiltInAttribute.priority) {
            switch (groupValue) {
                case "1_low":
                    tempTask.priority = "low";
                    break;
                case "2_medium":
                    tempTask.priority = "medium";
                    break;
                case "3_high":
                    tempTask.priority = "high";
                    break;
                case "4_urgent":
                    tempTask.priority = "urgent";
                    break;
                default:
                    tempTask.priority = null;
            }
        } else if (groupingProperty === BuiltInAttribute.dueDate) {
            if (groupValue === "3_Tomorrow") {
                tempTask.dueDate = format(addDays(new Date(), 1), "yyyy-MM-dd");
            } else if (groupValue === "2_Today") {
                tempTask.dueDate = format(new Date(), "yyyy-MM-dd");
            } else if (groupValue === "0_Overdue") {
                tempTask.dueDate = format(subDays(new Date(), 1), "yyyy-MM-dd");
            } else if (groupValue === "4_This week") {
                tempTask.dueDate = format(endOfWeek(new Date(), { weekStartsOn }), "yyyy-MM-dd");
            } else if (groupValue === "5_Next week") {
                tempTask.dueDate = format(endOfWeek(addWeeks(new Date(), 1), { weekStartsOn }), "yyyy-MM-dd");
            } else if (groupValue === "6_Later") {
                tempTask.dueDate = format(endOfMonth(addMonths(new Date(), 1)), "yyyy-MM-dd");
            } else {
                tempTask.dueDate = null;
            }
        } else if (groupingProperty === BuiltInAttribute.assignee) {
            const assigneeIds = groupValue.split(", ").map((x: string) => x.split("_")[0]);
            if (users && users.length > 0) {
                const assignees = assigneeIds.map((id: string) => users.find((user) => user.id === id));
                if (assignees.length > 0) {
                    tempTask.assignees = assignees;
                }
            }
        } else if (groupingProperty === BuiltInAttribute.lists) {
            const listIds = groupValue.split(", ").map((x: string) => parseInt(x.split("_")[0], 10));
            if (taskLists && taskLists.length > 0) {
                const lists = listIds.map((id: number) => taskLists.find((list) => list.id === id));
                if (lists.length > 0) {
                    tempTask.taskLists = lists;
                }
            }
        } else if (groupingProperty === BuiltInAttribute.parentTask) {
            const splits = groupValue.split("___");
            const parentId = parseInt(splits[0], 10);
            const parentTask = JSON.parse(splits[1]);

            if (parentId) {
                tempTask.parentTask = {
                    id: parentId,
                    title: parentTask.title,
                    number: parentTask.number,
                    key: crypto.randomUUID(),
                };
            }
        } else if (groupingProperty === BuiltInAttribute.createdBy) {
            // the created user is already assigned
        } else {
            tempTask[groupingProperty] = groupValue;
        }

        if (queryParams.type === "assigned-to-me" && tempTask.assignees.length === 0) {
            tempTask.assignees = [currentUser];
        }
    }

    tempTask._groupValue = groupValue ?? null;

    return tempTask;
}
